<script setup>
import PinAction from './PinAction.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import Avatar from 'next/avatar/Avatar.vue';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], default: null },
  assignee: { type: Object, default: null },
  compact: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  // Modo trilho (V3): gatilho de 36px (avatar de quem atende, ou pessoa com
  // ponto vermelho quando ninguem e dono). A pilula nao cabe no trilho.
  rail: { type: Boolean, default: false },
  // Acao de fixar no topo dentro do popover (V3): so onde o cabecalho pede.
  pinnable: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'close']);

const { t } = useI18n();
const store = useStore();
const { agentsList } = useAgentsList(true);
const saving = ref(false);

const currentUser = computed(() => store.getters.getCurrentUser);
const assigneeId = computed(() =>
  props.assignee?.id != null ? Number(props.assignee.id) : null
);
const firstName = computed(() => {
  if (!props.assignee?.name) return '';
  return props.assignee.name.split(' ')[0];
});
const displayName = user => {
  if (!user) return t('CONVERSATION.HEADER.UNASSIGNED');
  return Number(user.id) === Number(currentUser.value?.id)
    ? t('CONVERSATION_PARTICIPANTS.YOU')
    : user.name;
};

watch(
  () => props.inboxId,
  id => {
    if (id) store.dispatch('inboxAssignableAgents/fetch', [id]);
  },
  { immediate: true }
);

const pickAgent = async agent => {
  if (saving.value) return;
  const nextId = agent?.id && Number(agent.id) !== 0 ? Number(agent.id) : 0;
  if (
    nextId === assigneeId.value ||
    (nextId === 0 && assigneeId.value == null)
  ) {
    emit('close');
    return;
  }

  saving.value = true;
  try {
    await store.dispatch('assignAgent', {
      conversationId: props.conversationId,
      agentId: nextId || null,
    });
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
    emit('close');
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <ChipPopover :open="open" width-class="w-64" @close="emit('close')">
    <template #trigger>
      <button
        v-if="rail"
        type="button"
        data-testid="header-assignee-pill"
        class="relative flex size-9 items-center justify-center rounded-xl hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2': open }"
        :title="t('CONVERSATION.HEADER.CHANGE_ASSIGNEE')"
        @click="emit('toggle')"
      >
        <Avatar
          v-if="assignee"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="26"
        />
        <span
          v-else
          class="i-ph-user size-4 text-n-slate-11"
          aria-hidden="true"
        />
        <span
          v-if="!assignee"
          class="absolute right-1 top-1 size-2 rounded-full bg-n-ruby-9 ring-2 ring-n-surface-1"
          aria-hidden="true"
        />
      </button>
      <button
        v-else
        type="button"
        data-testid="header-assignee-pill"
        class="flex h-8 items-center gap-1.5 rounded-full pl-1.5 pr-2"
        :class="
          assignee
            ? ['hover:bg-n-alpha-2', { 'bg-n-alpha-2': open }]
            : 'bg-n-ruby-9/15 text-n-ruby-11 hover:bg-n-ruby-9/25'
        "
        :title="t('CONVERSATION.HEADER.CHANGE_ASSIGNEE')"
        :aria-label="
          assignee
            ? t('CONVERSATION.HEADER.CHANGE_ASSIGNEE')
            : t('CONVERSATION.HEADER.UNASSIGNED')
        "
        :disabled="saving"
        @click="emit('toggle')"
      >
        <Avatar
          v-if="assignee"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="20"
          rounded-full
          class="shrink-0"
        />
        <span
          v-else
          class="flex size-5 items-center justify-center rounded-full bg-n-ruby-9 text-[9px] font-bold text-white"
          aria-hidden="true"
        >
          {{ t('CONVERSATION.HEADER.UNASSIGNED_MARK') }}
        </span>
        <span
          v-if="!compact"
          class="text-[12.5px] font-bold"
          :class="assignee ? 'text-n-slate-12' : 'text-n-ruby-11'"
        >
          {{ assignee ? firstName : t('CONVERSATION.HEADER.UNASSIGNED') }}
        </span>
        <span
          class="i-ph-caret-down size-3.5"
          :class="assignee ? 'text-n-slate-10' : 'text-n-ruby-10'"
          aria-hidden="true"
        />
      </button>
    </template>

    <b
      class="block px-1 pb-1 pt-0.5 text-[11px] font-bold uppercase tracking-wide text-n-slate-10"
    >
      {{ t('CONVERSATION.HEADER.ASSIGNEE_POPOVER_TITLE') }}
    </b>
    <button
      v-for="agent in agentsList"
      :key="agent.id"
      type="button"
      class="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left hover:bg-n-solid-2 disabled:opacity-50"
      :disabled="saving"
      @click="pickAgent(agent)"
    >
      <Avatar
        :name="agent.name"
        :src="agent.thumbnail"
        :size="28"
        rounded-full
        class="shrink-0"
      />
      <span class="flex-1 truncate text-[13px] font-medium text-n-slate-12">
        {{ displayName(agent) }}
      </span>
      <span
        v-if="Number(agent.id) === assigneeId"
        class="i-ph-check size-4 text-n-teal-11"
        aria-hidden="true"
      />
    </button>
    <template v-if="pinnable" #actions>
      <PinAction modulo="agent" @done="emit('close')" />
    </template>
  </ChipPopover>
</template>
